import { createRootRoute, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import {
  Toaster,
  useAuth,
  AuthBootstrapper,
  SidebarProvider,
  SidebarInset,
  AllRightsReservedFooter,
  TopNavbar,
  Sentry,
} from '@org/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTrpcClient, trpc } from '@org/clients/school';
import { createGraphTrpcClient, graphTrpc } from '@org/clients/graph';
import { createCommunityTrpcClient, communityTrpc } from '@org/clients/community';
import React, { useMemo, useRef, useState } from 'react';
import superjson from 'superjson';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { AdminSidebar } from '../app/components/navbar';
import { useTranslation } from 'react-i18next';
import { LocaleProvider } from '@org/ui';

// Create a proper React component with a capital letter name
function TrpcProvider({ children }: { children: React.ReactNode }) {
  const { getAccessToken } = useAuth();
  const [queryClient] = useState(() => new QueryClient());
  const trpcClient = useMemo(() => {
    return createTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3333/api',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const graphClient = useMemo(() => {
    return createGraphTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_GRAPH_BACKEND_URL ?? 'http://localhost:3334/graph',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const communityClient = useMemo(() => {
    return createCommunityTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_COMMUNITY_BACKEND_URL ?? 'http://localhost:3333/community',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <graphTrpc.Provider client={graphClient} queryClient={queryClient}>
        <communityTrpc.Provider client={communityClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <AuthBootstrapper />
            {children}
          </QueryClientProvider>
        </communityTrpc.Provider>
      </graphTrpc.Provider>
    </trpc.Provider>
  );
}

function RootLayoutInner() {
  const { user } = useAuth();
  const { t } = useTranslation('admin');
  const fallbackName = t('layout.fallbacks.userName');
  const displayName = user?.firstName
    ? [user.firstName, user.lastName].filter(Boolean).join(' ')
    : user?.email ?? fallbackName;

  return (
    <TrpcProvider>
      <LocaleProvider>
        <SidebarProvider defaultOpen={false} className="bg-[#f8fafc]">
          <AdminSidebar />
          <SidebarInset className="bg-[#f8fafc]">
            <div className="flex min-h-svh flex-col">
              <TopNavbar
                eyebrowText={t('brand.name')}
                displayName={displayName}
                bgClass="bg-white/90"
                user={user}
                app="admin"
              />
              <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10">
                <div className="mx-auto w-full max-w-6xl">
                  <Outlet />
                </div>
              </div>
              <AllRightsReservedFooter className="bg-[#f8fafc]" />
              <TanStackRouterDevtools />
              <Toaster />
            </div>
          </SidebarInset>
        </SidebarProvider>
      </LocaleProvider>
    </TrpcProvider>
  );
}

function RootLayout() {
  const isBypassEnabled = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';
  const { t } = useTranslation('admin');
  const redirectMessageRef = useRef(t('common.nav.redirecting'));
  redirectMessageRef.current = t('common.nav.redirecting');

  const GuardedRoot = useMemo(() => {
    if (isBypassEnabled) {
      return RootLayoutInner;
    }

    const RedirectingMessage = () => (
      <div className="flex min-h-screen items-center justify-center">
        <span>{redirectMessageRef.current}</span>
      </div>
    );

    return withAuthenticationRequired(RootLayoutInner, {
      onRedirecting: () => <RedirectingMessage />,
    });
  }, [isBypassEnabled]);

  const GuardComponent = GuardedRoot;

  return <GuardComponent />;
}

function RouteErrorComponent({ error }: { error: Error }) {
  Sentry.captureException(error, { tags: { source: 'tanstack-router-root' } });
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h2>This page failed to load</h2>
      <p>Please refresh to try again.</p>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorComponent,
})
